import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getNotificationTarget } from './NotificationItem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Logique notifications partagée (cloche + page) : chargement, lecture,
   décision inline sur les demandes d'adhésion, navigation vers la cible. */
export const useNotifications = ({ category, onNavigate } = {}) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [joinDecisions, setJoinDecisions] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = category ? `?category=${category}` : '';
      const response = await axios.get(`${API}/notifications${qs}`, { withCredentials: true });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [category]);

  const markRead = (notification) => {
    if (notification.is_read) return;
    axios.post(`${API}/notifications/${notification.notification_id}/read`, null, { withCredentials: true })
      .then(() => window.dispatchEvent(new Event('notifications:refresh'))).catch(() => {});
    setNotifications((prev) => prev.map((n) => (n.notification_id === notification.notification_id ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    try {
      const qs = category ? `?category=${category}` : '';
      await axios.post(`${API}/notifications/read-all${qs}`, null, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event('notifications:refresh'));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const respondJoinRequest = async (e, notification, accept) => {
    e.stopPropagation();
    const { community_id: cid, request_id: rid } = notification.data || {};
    if (!cid || !rid) return;
    setJoinDecisions((prev) => ({ ...prev, [notification.notification_id]: 'loading' }));
    try {
      const { data } = await axios.post(`${API}/communities/${cid}/join-requests/${rid}/respond`, { accept }, { withCredentials: true });
      setJoinDecisions((prev) => ({ ...prev, [notification.notification_id]: data.status || (accept ? 'accepted' : 'rejected') }));
      markRead(notification);
    } catch (err) {
      setJoinDecisions((prev) => ({ ...prev, [notification.notification_id]: 'error' }));
    }
  };

  const open = (notification) => {
    markRead(notification);
    const target = getNotificationTarget(notification);
    if (target && typeof target === 'object' && target.external) window.open(target.external, '_blank', 'noopener');
    else if (target) navigate(target);
    onNavigate && onNavigate();
  };

  return { notifications, loading, unreadCount, joinDecisions, load, markAllAsRead, respondJoinRequest, open };
};
